import React, { useEffect, useState } from 'react';
import { safeArray } from '../../components/ErrorBoundary';
import { submittalsAPI } from '../../services/authService';
import { formatDateIN } from '../../utils/dateFormat';
import ProjectNameText from '../../components/ProjectNameText';
import SubmittalStatusBadge from './SubmittalStatusBadge';

const SubmittalDashboard = ({ onViewSubmittal, onNavigate }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const resp = await submittalsAPI.getSummary();
        if (!cancelled) setSummary(resp?.data || null);
      } catch (err) {
        console.warn('Submittal summary unavailable:', err?.message || err);
        if (!cancelled) setSummary(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const kpis = [
    { label: 'Total Submittals', value: summary?.total_submittals ?? 0, tone: '#6D28D9' },
    { label: 'Pending Internal', value: summary?.pending_internal ?? 0, tone: '#7c3aed' },
    { label: 'Pending Consultant', value: summary?.pending_consultant ?? 0, tone: '#F59E0B' },
    { label: 'Pending Client', value: summary?.pending_client ?? 0, tone: '#0891b2' },
    { label: 'Returned', value: summary?.returned_for_revision ?? 0, tone: '#b45309' },
    { label: 'Overdue', value: summary?.overdue ?? 0, tone: '#b91c1c' },
    { label: 'Approved', value: (summary?.approved ?? 0) + (summary?.approved_with_comments ?? 0), tone: '#047857' },
    { label: 'Rejected', value: summary?.rejected ?? 0, tone: '#dc2626' },
  ];

  const recent = safeArray(summary?.recent_submittals);
  const overdueRows = recent.filter((r) => r.is_overdue);

  if (loading) {
    return <div className="loading-state"><p>Loading submittal dashboard...</p></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Submittal Dashboard</h2>
          <p style={{ color: '#6b7280', margin: 0 }}>Overview of submittal status, pending actions, and overdue items.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {typeof onNavigate === 'function' && (
            <>
              <button type="button" className="btn-secondary btn-sm" onClick={() => onNavigate('submittal-register')}>Open Register</button>
              <button type="button" className="btn-primary btn-sm" onClick={() => onNavigate('submittal-create')}>Create Submittal</button>
            </>
          )}
        </div>
      </div>

      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        {kpis.map((kpi) => (
          <div key={kpi.label} className="kpi-card">
            <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 4px' }}>{kpi.label}</p>
            <p style={{ fontSize: 28, fontWeight: 700, color: kpi.tone, margin: 0 }}>{kpi.value}</p>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="table-container">
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Recent Submittals</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Ref</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: '#6b7280' }}>No recent submittals</td></tr>
              ) : recent.slice(0, 8).map((row) => (
                <tr key={row.id} style={{ cursor: typeof onViewSubmittal === 'function' ? 'pointer' : 'default' }} onClick={() => onViewSubmittal?.(row.id)}>
                  <td style={{ fontWeight: 500, color: '#7C3AED' }}>{row.submittal_no || row.base_number || `#${row.id}`}</td>
                  <td>{row.subject}</td>
                  <td><SubmittalStatusBadge status={row.status} isOverdue={row.is_overdue} /></td>
                  <td>{formatDateIN(row.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="table-container">
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Overdue / At Risk</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Ref</th>
                <th>Project</th>
                <th>Due</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {overdueRows.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: '#6b7280' }}>No overdue submittals in recent list</td></tr>
              ) : overdueRows.map((row) => (
                <tr key={row.id} style={{ cursor: typeof onViewSubmittal === 'function' ? 'pointer' : 'default' }} onClick={() => onViewSubmittal?.(row.id)}>
                  <td style={{ fontWeight: 500, color: '#7C3AED' }}>{row.submittal_no || `#${row.id}`}</td>
                  <td><ProjectNameText as="span">{row.project?.name || '—'}</ProjectNameText></td>
                  <td>{formatDateIN(row.due_date)}</td>
                  <td><SubmittalStatusBadge status={row.status} isOverdue /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SubmittalDashboard;
